import { Link } from 'react-router-dom';

function chatPath(conversationId: string, messageId?: string | null): string {
  const base = `/conversations/${conversationId}`;
  if (!messageId) return base;
  return `${base}?mensaje=${encodeURIComponent(messageId)}`;
}
import { bookingSourceLabel } from '@/calendar/bookingSource';
import {
  airbnbStayKind,
  bookingCardStatusLabel,
  bookingCardTitle,
  formatBookingDateRange,
  isOtaBooking,
  truncateId,
} from '@/calendar/bookingListDisplay';
import { bookingStatusLabel } from '@/calendar/pwaBookingDetail';
import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import { BookingSourceIcon } from '@/components/BookingSourceIcon';

function statusColor(status: string): string {
  if (status === 'confirmed') return '#16a34a';
  if (status === 'pending_deposit') return '#d97706';
  if (status === 'human_supervision') return '#7c3aed';
  if (status.includes('cancel')) return '#94a3b8';
  return '#64748b';
}

function badgeColor(booking: PwaCalendarBooking): string {
  if (isOtaBooking(booking)) return '#b45309';
  return statusColor(booking.status);
}

function resourceDotColor(booking: PwaCalendarBooking): string {
  return booking.resourceColor && /^#[0-9A-Fa-f]{6}$/.test(booking.resourceColor)
    ? booking.resourceColor
    : statusColor(booking.status);
}

type Props = {
  booking: PwaCalendarBooking;
  onOpen: () => void;
};

export function BookingListCard({ booking, onOpen }: Props) {
  const ota = isOtaBooking(booking);
  const chatId = booking.conversationId?.trim() || null;
  const badge = bookingCardStatusLabel(booking, bookingStatusLabel);
  const stayKind = airbnbStayKind(booking);

  return (
    <button type="button" className="bookings-search-page__item" onClick={onOpen}>
      <div
        className="bookings-search-page__item-dot"
        style={{ background: resourceDotColor(booking) }}
      />
      <div className="bookings-search-page__item-body">
        <div className="bookings-search-page__item-name">
          {bookingCardTitle(booking)}
          <span
            className={stayKind ? `bookings-search-page__badge--${stayKind}` : undefined}
            style={{ color: badgeColor(booking) }}
          >
            {badge}
          </span>
        </div>
        <div className="bookings-search-page__item-meta">
          <BookingSourceIcon
            bookingOrigin={booking.bookingOrigin}
            otaChannel={booking.otaChannel}
            conversationChannel={booking.conversationChannel}
            size={14}
          />
          <span>
            {bookingSourceLabel(booking)}
            {booking.agentName ? ` · ${booking.agentName}` : ''}
          </span>
        </div>
        <div className="bookings-search-page__item-dates">
          {formatBookingDateRange(booking.startDate, booking.endDate)}
        </div>
        {booking.alsoBlockedOnAirbnb ?
          <div className="bookings-search-page__item-ref">También bloqueado en Airbnb</div>
        : null}
        <div className="bookings-search-page__item-ref">
          {ota && booking.externalId ? (
            <span title={booking.externalId}>iCal {truncateId(booking.externalId, 16)}</span>
          ) : null}
          {ota && booking.externalId && booking.publicReference ? ' · ' : null}
          {booking.publicReference ? `Código ${booking.publicReference}` : null}
          {!ota && !booking.publicReference && !chatId ? (
            <span>ID {truncateId(booking.id, 8)}</span>
          ) : null}
        </div>
        {chatId ? (
          <>
            {booking.sourceMessagePreview ? (
              <div className="bookings-search-page__item-ref">“{booking.sourceMessagePreview}”</div>
            ) : null}
            <Link
              to={chatPath(chatId, booking.sourceMessageId)}
              className="bookings-search-page__chat-link"
              onClick={(e) => e.stopPropagation()}
            >
              Ver mensaje del chat
            </Link>
          </>
        ) : null}
      </div>
      <span className="bookings-search-page__item-arrow">›</span>
    </button>
  );
}
